/**
 * Response shapes of the super-admin network API (`services/pms/admin/network/*`,
 * workstream B's API.md). The nouns themselves live in `network.model.ts`; these
 * are the rows and results the admin pages read.
 */
import type {
    NetworkBrand,
    NetworkBrandStatus,
    NetworkCollection,
    NetworkColour,
    NetworkFormat,
    NetworkLink,
    NetworkLinkEvidence,
    NetworkLinkMethod,
    NetworkLinkStatus,
    NetworkProduct,
    NetworkProductStatus,
} from '../models/network.model';

export const NETWORK_BRAND_STATUSES: NetworkBrandStatus[] = ['unclaimed', 'claimed', 'partner'];
export const NETWORK_PRODUCT_STATUSES: NetworkProductStatus[] = ['draft', 'published'];
export const NETWORK_LINK_STATUSES: NetworkLinkStatus[] = ['suggested', 'confirmed', 'rejected'];
export const NETWORK_LINK_METHODS: NetworkLinkMethod[] = ['ean', 'ref', 'name', 'visual', 'import', 'manual'];

/**
 * The closed vocabularies, mirrored from the API (`insight-contract.ts`). The
 * server snaps whatever it is sent onto these, so the edit form offers exactly
 * these and nothing that would be silently rewritten on save.
 */
export const NETWORK_COLOR_FAMILIES = [
    'white', 'beige', 'brown', 'grey', 'black',
    'blue', 'green', 'red', 'orange', 'yellow', 'pink', 'purple',
    'metallic', 'multicolour',
] as const;
export const NETWORK_MATERIALS = [
    'ceramic', 'porcelain', 'stone', 'marble', 'terrazzo', 'concrete',
    'wood', 'laminate', 'vinyl', 'metal', 'glass', 'textile', 'leather', 'rattan',
    'composite', 'other',
] as const;
export const NETWORK_APPEARANCES = [
    'marble', 'stone', 'wood', 'concrete', 'terrazzo', 'terracotta', 'cement-tile', 'zellige',
    'metal', 'glass', 'fabric', 'leather', 'rattan',
    'plain', 'pattern', 'other',
] as const;
export const NETWORK_FINISHES = [
    'matte', 'satin', 'polished', 'glossy', 'textured',
    'brushed', 'honed', 'distressed', 'ribbed', 'metallic',
] as const;

export interface Paged<T> {
    items: T[];
    total: number;
    page: number;
    limit: number;
}

export interface BrandCounts {
    collections: number;
    products: number;
    published: number;
    retailers: number;
}

export type BrandRow = NetworkBrand & { counts: BrandCounts };

export interface BrandDetail {
    brand: NetworkBrand;
    counts: Partial<BrandCounts> & Record<string, number>;
    collections: (NetworkCollection & { products: number })[];
}

export interface BrandInput {
    name?: string;
    slug?: string;
    aliases?: string[];
    country?: string;
    website?: string;
    logoUrl?: string;
    description?: string;
    materials?: string[];
    status?: NetworkBrandStatus;
}

export interface CollectionInput {
    name?: string;
    slug?: string;
    material?: string;
    appearance?: string;
    description?: string;
    coverImageUrl?: string;
}

export type ProductRow = NetworkProduct & { brandName?: string; collectionName?: string; imageUrl: string | null };

export interface ProductRetailerLink {
    linkId: string;
    vendorId: string;
    vendorName: string;
    productUuid: string;
    productId?: string;
    productName: string | null;
    sku?: string;
    imageUrl: string | null;
    method: NetworkLinkMethod;
    score: number;
    status: NetworkLinkStatus;
    decidedBy?: string;
    decidedAt?: string;
    evidence: NetworkLinkEvidence;
}

export interface ProductDetail {
    product: NetworkProduct;
    brand: NetworkBrand | null;
    collection: NetworkCollection | null;
    imageUrl: string | null;
    retailers: ProductRetailerLink[];
}

export interface ProductInput {
    brandId?: string;
    /** `null` detaches the product from its collection (PATCH only). */
    collectionId?: string | null;
    name?: string;
    ref?: string;
    eans?: string[];
    format?: NetworkFormat;
    finish?: string;
    colour?: NetworkColour;
    material?: string;
    appearance?: string;
    pattern?: string;
    tags?: string[];
}

export interface ProductQuery {
    q?: string;
    brandId?: string;
    collectionId?: string;
    status?: NetworkProductStatus;
    digitized?: boolean;
    /** 1-based. */
    page?: number;
    limit?: number;
}

export interface RetailerSummary {
    vendorId: string;
    name: string;
    alias?: string;
    products: number;
    linkedProducts: number;
    suggestedProducts: number;
}

export interface RetailerProduct {
    uuid: string;
    id: string;
    name: string;
    sku?: string;
    brand?: string;
    imageUrl: string | null;
    format?: NetworkFormat;
    colour?: { name: string; hex?: string };
    link?: {
        _id: string;
        status: NetworkLinkStatus;
        networkProductId: string;
        networkProductName?: string;
        score: number;
        method: string;
    } | null;
}

export interface VisualState {
    on: boolean;
    model?: string;
    reason?: string;
    thresholds?: { same: number; similar: number };
}

export interface MatchVendorResult {
    vendorId: string;
    vendorName: string;
    suggested: number;
    confirmed: number;
    skipped: number;
    byMethod: Record<string, number>;
    products: number;
    unmatched: number;
    visual: VisualState;
    durationMs: number;
}

export interface LinkRow {
    _id: string;
    status: NetworkLinkStatus;
    method: NetworkLinkMethod;
    score: number;
    evidence: NetworkLinkEvidence;
    decidedBy?: string;
    decidedAt?: string;
    createdAt: string;
    vendor: { _id: string; name: string };
    /** Null when the retailer's ProductV2 was deleted since. */
    retailer: {
        productUuid: string;
        productId?: string;
        name: string;
        sku?: string;
        brand?: string;
        imageUrl: string | null;
        format?: NetworkFormat;
        colour?: { name: string; hex?: string };
        eans: string[];
    } | null;
    network: {
        _id: string;
        uuid: string;
        name: string;
        ref?: string;
        eans: string[];
        format?: NetworkFormat;
        colour?: NetworkColour;
        imageUrl: string | null;
        status: NetworkProductStatus;
        retailerCount: number;
        brand: { _id: string; name: string } | null;
        collection: { _id: string; name: string } | null;
    } | null;
    /** Other open suggestions for the same retailer product. */
    alternatives: number;
}

export type LinkSort = 'score_desc' | 'score_asc' | 'recent';

export interface LinkQuery {
    status?: NetworkLinkStatus;
    method?: NetworkLinkMethod;
    vendorId?: string;
    brandId?: string;
    minScore?: number;
    maxScore?: number;
    q?: string;
    page?: number;
    limit?: number;
    sort?: LinkSort;
}

export interface LinkUpdateResult {
    link: NetworkLink;
    /** Other links of that retailer product rejected by a confirm. */
    affected: number;
}

export interface BulkLinkResult {
    updated: number;
    skipped: number;
    conflicts: { id: string; reason: string }[];
}

export interface DiscoverCluster {
    networkProductId?: string;
    name: string;
    brand: { id?: string; name: string; created: boolean } | null;
    format?: NetworkFormat;
    members: { vendorId: string; vendorName: string; productUuid: string; name: string; imageUrl: string | null; score: number }[];
    created: boolean;
    reason?: string;
}

export interface DiscoverResult {
    clusters: DiscoverCluster[];
    created: number;
    examined: number;
    visual: VisualState;
}

export interface EnsureResult {
    model: string | null;
    embedded: number;
    skipped: number;
    failed: number;
    unavailable?: string;
}

export interface BackfillResult {
    network: EnsureResult;
    vendors: (EnsureResult & { vendorId: string; name: string })[];
    unavailable?: string;
}

export interface BrandCoverage {
    brandId: string;
    slug: string;
    name: string;
    status: string;
    collections: number;
    products: number;
    published: number;
    digitized: number;
    carried: number;
    retailers: number;
    confirmedLinks: number;
    suggestedLinks: number;
}

export interface NetworkEval {
    at: string;
    model: string | null;
    visual: boolean;
    vendors: string[];
    overall: { precision: number; recall: number; f1: number; tp: number; fp: number; fn: number };
    autoConfirmed: { count: number; precision: number };
    byMethod: Record<string, { links: number; precision: number }>;
    byRetailer: Record<string, { precision: number; recall: number; products: number }>;
}

export interface NetworkStats {
    brands: { total: number; byStatus: Record<NetworkBrandStatus, number> };
    collections: { total: number };
    products: { total: number; byStatus: Record<NetworkProductStatus, number>; digitized: number; digitizedShare: number };
    links: {
        total: number;
        byStatus: Record<NetworkLinkStatus, number>;
        confirmedByMethod: Record<string, number>;
        suggestedByMethod: Record<string, number>;
    };
    retailers: { total: number; linked: number };
    coverage: BrandCoverage[];
    embeddings: { model: string; kind: 'product' | 'network'; count: number }[];
    surfaceId: { ok: boolean; model?: string; thresholds?: { same: number; similar: number } };
    review: {
        decided: number;
        confirmed: number;
        rejected: number;
        byMethod: Record<string, { confirmed: number; rejected: number; acceptance: number | null }>;
    };
    lastEval: NetworkEval | null;
}
